import {
  addEventListener,
  WixPatternsContainer,
  WixPatternsError,
  DataExtensionState,
  FiltersMap,
  TaskState,
  withoutDefaults,
} from '@wix/bex-core';
import { ToolbarCollectionState } from '../../state';
import { DataExtensionType } from './DataExtension';
import {
  cairoErrorInLoadingAComponent,
  cairoTryAgainClicked,
} from '@wix/bex-core/bi';

export interface CollectionDataExtensionStateParams<T, F extends FiltersMap> {
  container: WixPatternsContainer;
  toolbar: ToolbarCollectionState<T, F>;
  DataExtension: DataExtensionType;
}

export class CollectionDataExtensionState<T, F extends FiltersMap> {
  readonly dataExtension;
  readonly toolbar;
  readonly DataExtension;
  readonly initTask = new TaskState();
  constructor(params: CollectionDataExtensionStateParams<T, F>) {
    this.toolbar = params.toolbar;
    this.DataExtension = params.DataExtension;
    this.dataExtension = new DataExtensionState({
      container: params.container,
      fqdn: params.toolbar.collection.fqdn ?? {
        product: params.toolbar.collection.queryName,
      },
      reportBi: params.toolbar.reportBi,
      closeSidePanel: params.toolbar.closeSidePanel,
    });
  }

  _getFetchSchemasError(err: unknown) {
    return new WixPatternsError({
      errorCode: 'InitCustomFieldsDataExtensionFailed',
      originalException: err,
    });
  }

  _handleFetchSchemasError() {
    const error = this.dataExtension.initTask.status.error;

    if (!error) {
      return;
    }

    const cairoError = this._getFetchSchemasError(error);
    this.dataExtension.container.showToast?.({
      message: this.dataExtension.container.translate(
        'cairo.customFields.fetch.technicalError',
      ),
      action: {
        uiType: 'LINK',
        text: this.dataExtension.container.translate('cairo.toast.retry'),
        onClick: () => {
          this.dataExtension.reportBi(
            withoutDefaults(cairoTryAgainClicked)({
              actionName: 'Fetch extended fields',
              loaction: 'toast',
            }),
          );
          this.dataExtension
            .fetchSchemas()
            .catch(this._handleFetchSchemasError.bind(this));
        },
        removeToastOnClick: true,
      },
      type: 'WARNING',
      biName: 'cairo-fetch-schemas-error-toast',
    });

    this.dataExtension.container.internalMonitor.reportError({
      error: cairoError,
    });

    this.dataExtension.reportBi(
      cairoErrorInLoadingAComponent({
        errorType: cairoError.name,
      }),
    );
  }

  init() {
    const { initTask, dataExtension, toolbar } = this;
    const { customColumnsState } = toolbar;

    const disposers = [
      dataExtension.init(),
      ...(customColumnsState?.events
        ? [
            addEventListener(
              customColumnsState.events,
              'beforeInitialFetch',
              () => initTask.status.promise,
            ),
          ]
        : []),
    ];

    initTask.runOnce(async () => {
      dataExtension.initTask.runOnce();
      dataExtension.container.initTask.runOnce();

      await Promise.all([
        dataExtension.initTask.status.promise?.catch(() => {}),
        dataExtension.container.initTask.status.promise,
      ]);

      this._handleFetchSchemasError();
      toolbar.setNewExtendedColumns(dataExtension.availableCustomFields);
    });

    return () => {
      disposers.forEach((d) => d?.());
    };
  }
}
